import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { api } from '../api';
import { parseCurveCsv, summarizeMetrics } from '../lib/parseCurveCsv';
import ChargingCurveChart from '../components/ChargingCurveChart';

const EMPTY = { carMake: '', carModel: '', trimLevel: '', charger: '', cables: '', notes: '', videoUrl: '' };
const carLabel = (c) => `${c.make} ${c.model}${c.trim ? ' ' + c.trim : ''}`.trim();

// read an image file into a data URL (same approach as the ads admin)
function readImage(file) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) return reject(new Error('Filen er ikke et bilde.'));
    if (file.size > 5 * 1024 * 1024) return reject(new Error('Bildet er for stort (maks 5 MB).'));
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Kunne ikke lese bildet.'));
    r.readAsDataURL(file);
  });
}

// Admin: manage cars (with images) and upload charging sessions (CSV + video link).
function LadekurverAdmin() {
  const [items, setItems] = useState([]);   // sessions
  const [cars, setCars] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [extra, setExtra] = useState([{ label: '', value: '' }]);
  const [parsed, setParsed] = useState(null);
  const [fileName, setFileName] = useState('');
  const [editId, setEditId] = useState(null);
  const [newCar, setNewCar] = useState({ make: '', model: '', trim: '' });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [s, c] = await Promise.all([api('/api/charging-curves'), api('/api/curve-cars')]);
    if (s.ok) setItems(await s.json());
    if (c.ok) setCars(await c.json());
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFile = async (e) => {
    setErr(''); setMsg('');
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    try {
      setParsed(parseCurveCsv(await file.text()));
    } catch (ex) { setParsed(null); setErr(ex.message); }
  };

  const setExtraRow = (i, k, v) =>
    setExtra((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const reset = () => {
    setForm(EMPTY); setExtra([{ label: '', value: '' }]); setParsed(null);
    setFileName(''); setEditId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(''); setMsg('');
    if (!form.carMake.trim() || !form.carModel.trim()) return setErr('Bilmerke og modell er påkrevd.');
    if (!form.charger.trim()) return setErr('Lader er påkrevd.');
    if (!parsed) return setErr('Last opp en gyldig CSV først.');
    const extraObj = {};
    for (const r of extra) if (r.label.trim() && r.value.trim()) extraObj[r.label.trim()] = r.value.trim();
    const payload = { ...form, curve: parsed.curve, metrics: parsed.metrics, extra: extraObj };
    setSaving(true);
    try {
      const res = await api(editId ? `/api/charging-curves/${editId}` : '/api/charging-curves', {
        method: editId ? 'PUT' : 'POST', body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Lagring feilet');
      setMsg(editId ? 'Ladeøkt oppdatert.' : 'Ladeøkt publisert.');
      reset(); load();
    } catch (ex) { setErr(ex.message); } finally { setSaving(false); }
  };

  const handleEdit = (it) => {
    setEditId(it.id);
    setForm({
      carMake: it.carMake, carModel: it.carModel, trimLevel: it.trimLevel,
      charger: it.charger, cables: it.cables, notes: it.notes, videoUrl: it.videoUrl || ''
    });
    setParsed({ curve: it.curve, metrics: it.metrics, hasAmp: it.hasAmp });
    setFileName(`(eksisterende kurve, ${it.curve.length} punkter)`);
    const ex = Object.entries(it.extra || {}).map(([label, value]) => ({ label, value: String(value) }));
    setExtra(ex.length ? ex : [{ label: '', value: '' }]);
    setMsg(''); setErr('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Slette denne ladeøkten?')) return;
    const res = await api(`/api/charging-curves/${id}`, { method: 'DELETE' });
    if (res.ok) { if (editId === id) reset(); load(); }
  };

  // ---- car / image management ----
  const uploadCarImage = async (carId, file) => {
    setErr(''); setMsg('');
    try {
      const image = await readImage(file);
      const res = await api(`/api/curve-cars/${carId}`, { method: 'PUT', body: JSON.stringify({ image }) });
      if (!res.ok) throw new Error((await res.json()).error || 'Opplasting feilet');
      setMsg('Bilde lagret.'); load();
    } catch (ex) { setErr(ex.message); }
  };
  const clearCarImage = async (carId) => {
    const res = await api(`/api/curve-cars/${carId}`, { method: 'PUT', body: JSON.stringify({ image: null }) });
    if (res.ok) load();
  };
  const deleteCar = async (carId) => {
    if (!window.confirm('Slette bilen og alle dens ladeøkter?')) return;
    const res = await api(`/api/curve-cars/${carId}`, { method: 'DELETE' });
    if (res.ok) load();
  };
  const addCar = async () => {
    setErr(''); setMsg('');
    if (!newCar.make.trim() || !newCar.model.trim()) return setErr('Bilmerke og modell er påkrevd.');
    const res = await api('/api/curve-cars', { method: 'POST', body: JSON.stringify(newCar) });
    if (res.ok) { setNewCar({ make: '', model: '', trim: '' }); load(); }
    else setErr((await res.json()).error || 'Kunne ikke legge til bil.');
  };

  const inputCls = 'w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white placeholder-gray-500 focus:outline-none focus:border-teal-500';
  const labelCls = 'block text-sm text-gray-300 mb-1';

  return (
    <div className="min-h-screen bg-slate-900 pt-8 pb-16 px-4">
      <SEO title="Ladekurver – admin" path="/admin/ladekurver" noindex />
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Ladekurver</h1>
            <p className="text-sm text-gray-400 mt-1">Biler med bilder, og ladeøkter (CSV + video).</p>
          </div>
          <Link to="/admin/meny" className="px-4 py-2 bg-slate-800 text-gray-300 rounded-xl hover:bg-slate-700 text-sm">← Admin</Link>
        </div>

        {/* ---- Session upload form ---- */}
        <form onSubmit={handleSubmit} className="bg-slate-800 bg-opacity-60 border border-slate-700 rounded-2xl p-6 mb-10">
          <h2 className="text-lg font-bold text-white mb-4">
            {editId ? `Rediger ladeøkt #${editId}` : 'Ny ladeøkt'}
          </h2>

          <label className={labelCls}>CSV-fil (charging-curver <code>_data.csv</code>)</label>
          <div className="flex items-center gap-3 mb-2">
            <label className="px-4 py-2 bg-teal-500 text-slate-900 font-semibold rounded-lg cursor-pointer hover:bg-teal-400">
              Velg fil<input type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" />
            </label>
            <span className="text-sm text-gray-400 truncate">{fileName || 'Ingen fil valgt'}</span>
          </div>

          {parsed && (
            <div className="bg-slate-900 rounded-xl p-4 mb-6">
              <ChargingCurveChart curve={parsed.curve} hasAmp={parsed.hasAmp} darkMode />
              <div className="flex flex-wrap gap-2 mt-3">
                {summarizeMetrics(parsed.metrics).map((mm) => (
                  <span key={mm.label} className="px-2.5 py-1 rounded bg-slate-800 text-xs text-gray-300">
                    {mm.label}: <span className="font-semibold text-white">{mm.value}</span>
                  </span>
                ))}
                <span className="px-2.5 py-1 rounded bg-slate-800 text-xs text-gray-300">
                  {parsed.curve.length} punkter · {parsed.hasAmp ? 'kW + A' : 'kun kW'}
                </span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Bilmerke *</label>
              <input className={inputCls} value={form.carMake} onChange={set('carMake')} placeholder="Zeekr" required list="curve-makes" /></div>
            <div><label className={labelCls}>Modell *</label>
              <input className={inputCls} value={form.carModel} onChange={set('carModel')} placeholder="7GT" required /></div>
            <div><label className={labelCls}>Utstyrsnivå / trim</label>
              <input className={inputCls} value={form.trimLevel} onChange={set('trimLevel')} placeholder="Core / Performance …" /></div>
            <div><label className={labelCls}>Lader *</label>
              <input className={inputCls} value={form.charger} onChange={set('charger')} placeholder="Alpitronic HYC400 …" required /></div>
            <div><label className={labelCls}>Kabler</label>
              <input className={inputCls} value={form.cables} onChange={set('cables')} placeholder="CCS, kjølt kabel …" /></div>
            <div><label className={labelCls}>Video-lenke</label>
              <input className={inputCls} value={form.videoUrl} onChange={set('videoUrl')} placeholder="https://youtube.com/…" type="url" /></div>
          </div>
          <datalist id="curve-makes">{[...new Set(cars.map((c) => c.make))].map((m) => <option key={m} value={m} />)}</datalist>

          <div className="mt-4"><label className={labelCls}>Notater</label>
            <textarea className={inputCls} rows={2} value={form.notes} onChange={set('notes')}
              placeholder="Batteritemperatur, forhold, forvarming, o.l." /></div>

          <div className="mt-4">
            <label className={labelCls}>Ekstra info (valgfritt)</label>
            {extra.map((r, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input className={inputCls} value={r.label} onChange={(e) => setExtraRow(i, 'label', e.target.value)} placeholder="Felt (f.eks. Utetemperatur)" />
                <input className={inputCls} value={r.value} onChange={(e) => setExtraRow(i, 'value', e.target.value)} placeholder="Verdi (f.eks. 16 °C)" />
                <button type="button" onClick={() => setExtra((r2) => r2.filter((_, idx) => idx !== i))} className="px-3 rounded-lg bg-slate-700 text-gray-300 hover:bg-slate-600">×</button>
              </div>
            ))}
            <button type="button" onClick={() => setExtra((r) => [...r, { label: '', value: '' }])} className="text-sm text-teal-400 hover:text-teal-300">+ Legg til felt</button>
          </div>

          {err && <p className="mt-4 text-red-400 text-sm">{err}</p>}
          {msg && <p className="mt-4 text-teal-400 text-sm">{msg}</p>}

          <div className="mt-6 flex gap-3">
            <button type="submit" disabled={saving} className="px-6 py-2.5 bg-teal-500 text-slate-900 font-bold rounded-xl hover:bg-teal-400 disabled:opacity-50">
              {saving ? 'Lagrer…' : editId ? 'Oppdater' : 'Publiser'}
            </button>
            {editId && <button type="button" onClick={reset} className="px-6 py-2.5 bg-slate-700 text-gray-300 rounded-xl hover:bg-slate-600">Avbryt</button>}
          </div>
        </form>

        {/* ---- Cars & images ---- */}
        <h2 className="text-xl font-bold text-white mb-4">Biler &amp; bilder ({cars.length})</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {cars.map((c) => (
            <div key={c.id} className="flex items-center gap-3 bg-slate-800 bg-opacity-60 border border-slate-700 rounded-xl p-3">
              {c.image
                ? <img src={c.image} alt={carLabel(c)} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                : <div className="w-16 h-16 rounded-lg bg-slate-700 flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold truncate">{carLabel(c)}</p>
                <p className="text-xs text-gray-400">{c.sessionCount} ladeøkt{c.sessionCount === 1 ? '' : 'er'}</p>
                <div className="flex gap-3 mt-1 text-xs">
                  <label className="text-teal-400 hover:text-teal-300 cursor-pointer">
                    {c.image ? 'Bytt bilde' : 'Last opp bilde'}
                    <input type="file" accept="image/*" className="hidden"
                      onChange={(e) => e.target.files[0] && uploadCarImage(c.id, e.target.files[0])} />
                  </label>
                  {c.image && <button onClick={() => clearCarImage(c.id)} className="text-gray-400 hover:text-gray-200">Fjern bilde</button>}
                  <button onClick={() => deleteCar(c.id)} className="text-red-400 hover:text-red-300">Slett bil</button>
                </div>
              </div>
            </div>
          ))}
          {cars.length === 0 && <p className="text-gray-400 text-sm">Ingen biler ennå — publiser en ladeøkt for å opprette en.</p>}
        </div>
        <div className="flex flex-wrap gap-2 mb-10">
          <input className={inputCls + ' max-w-[8rem]'} value={newCar.make} onChange={(e) => setNewCar({ ...newCar, make: e.target.value })} placeholder="Merke" />
          <input className={inputCls + ' max-w-[8rem]'} value={newCar.model} onChange={(e) => setNewCar({ ...newCar, model: e.target.value })} placeholder="Modell" />
          <input className={inputCls + ' max-w-[8rem]'} value={newCar.trim} onChange={(e) => setNewCar({ ...newCar, trim: e.target.value })} placeholder="Trim" />
          <button onClick={addCar} className="px-4 py-2 bg-slate-700 text-gray-200 rounded-lg hover:bg-slate-600 text-sm">+ Legg til bil</button>
        </div>

        {/* ---- Sessions list ---- */}
        <h2 className="text-xl font-bold text-white mb-4">Publiserte ladeøkter ({items.length})</h2>
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between bg-slate-800 bg-opacity-60 border border-slate-700 rounded-xl px-4 py-3">
              <div className="min-w-0">
                <p className="text-white font-semibold truncate">
                  {it.carMake} {it.carModel}{it.trimLevel ? ` · ${it.trimLevel}` : ''}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {it.charger} · {it.metrics?.soc_start ?? '?'}–{it.metrics?.soc_end ?? '?'} % · {it.hasAmp ? 'kW+A' : 'kW'}
                  {it.videoUrl ? ' · 🎬' : ''}
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => handleEdit(it)} className="px-3 py-1.5 text-sm bg-slate-700 text-gray-200 rounded-lg hover:bg-slate-600">Rediger</button>
                <button onClick={() => handleDelete(it.id)} className="px-3 py-1.5 text-sm bg-red-900 bg-opacity-50 text-red-300 rounded-lg hover:bg-opacity-80">Slett</button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-gray-400 text-sm">Ingen ladeøkter ennå.</p>}
        </div>
      </div>
    </div>
  );
}

export default LadekurverAdmin;
